import { CloseOutlined, EnvironmentFilled, LoadingOutlined } from '@ant-design/icons';
import { WaterbodyDetail, Waypoint } from '@omniafishing/core';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { XMLParser } from 'fast-xml-parser';
import _ from 'lodash';
import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useWaypoints } from '../../../../hooks/use_waypoints';
import { apiV1 } from '../../../../lib/api';
import { FlashMessageActions } from '../../../../redux/flash_message';
import Loading from '../../../loading/loading';
import { OmniaButton } from '../../../omnia_button/omnia_button';
import { WaypointMarker } from '../../waypoint_marker';
import { queryMapAtPoint, truncateTo6Decimal } from './waypoint_form_helper';
import styles from './waypoint_import_screen.less';

interface WaypointImportScreenProps {
  onClose: () => void;
  onShowImportScreenChange: (val: boolean) => void;
}

export interface WaypointLike {
  name: string;
  outing_at: string;
  lat: number;
  lng: number;
  comments: string;
  waypoint_type: string;
  waterbody?: WaterbodyDetail;
  waterbody_id?: string;
  metadata?: any;
}

const options = {
  ignoreAttributes: false,
  transformAttributeName: (attributeName: string) => {
    switch (attributeName) {
      case '@_lat':
        return 'lat';
      case '@_lon':
        return 'lng';
      default:
        return attributeName.replace(/^@_/, ''); // parser adds `@_` to all attributes
    }
  },
  isArray: (name: string) => {
    if (name === 'wpt') {
      return true;
    }
  },
};

export const WaypointImportScreen = React.memo((props: WaypointImportScreenProps) => {
  const { onClose, onShowImportScreenChange } = props;

  const { importWaypoints } = useWaypoints();
  const [waypointData, setWaypointData] = useState([]);
  const [fileLoaded, setFileLoaded] = useState(false);
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();

  const transformData = async (data: any, creator: string) => {
    const newWaypoints: WaypointLike[] = [];

    const waterbodyIdPromises = data.map((w: any) => {
      if (!w.lat || !w.lng) {
        return; // make no call to api if no lat or lng
      }
      const { lat, lng } = w;
      return queryMapAtPoint(parseFloat(lat), parseFloat(lng));
    });

    const waterbodyIdResults = await Promise.allSettled(waterbodyIdPromises);

    const waterbodyPromises: Promise<void>[] = [];
    const processedIds = new Set<string>();
    const waterbodies = new Map<string, WaterbodyDetail>();

    // fetchWaterbodyDetail for name on FE
    waterbodyIdResults.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value && !processedIds.has(result.value)) {
        processedIds.add(result.value);
        waterbodyPromises.push(
          apiV1
            .waterbodyFetch(result.value)
            .then((res) => {
              waterbodies.set(result.value, res.data.data);
            })
            .catch((err) => {
              console.error('Error fetching waterbody:', err);
              waterbodies.set(result.value, undefined);
            })
        );
      }
    });

    await Promise.allSettled(waterbodyPromises);

    data.forEach((rawWaypoint: any, index: number) => {
      const { name, lat, lng, time, cmt, desc } = rawWaypoint;
      const outing_at = dayjs(time).toISOString(); // converts to time of import if absent
      const parsedLat = truncateTo6Decimal(parseFloat(lat));
      const parsedLng = truncateTo6Decimal(parseFloat(lng));

      if (!parsedLat || !parsedLng) {
        // remove waypoints if no lat or lng
        console.warn('Missing latitude or longitude:', rawWaypoint);
        return;
      }
      const isCommentsName = name === cmt;
      const isDescriptionName = name === desc;
      const comments = isCommentsName || isDescriptionName ? null : cmt || desc;

      const waterbodyResult = waterbodyIdResults[index];
      let waterbodyId: string;
      if (waterbodyResult.status === 'fulfilled' && waterbodyResult.value) {
        waterbodyId = waterbodyResult.value;
      }

      const rawWaypointWithCreator = { ...rawWaypoint, creator };

      const newWaypoint = {
        comments,
        lat: parsedLat,
        lng: parsedLng,
        name,
        outing_at,
        raw_data: rawWaypointWithCreator,
        waterbody_id: waterbodyId,
        waterbody: waterbodies.get(waterbodyId), // waterbody needed for waypoints preview
        waypoint_type: 'marker',
      };

      newWaypoints.push(newWaypoint);
    });

    return newWaypoints;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFileLoaded(true);
        const xml = e.target.result as string;
        const parser = new XMLParser(options);
        try {
          const jsonObj = parser.parse(xml);
          const parsedWaypointData = jsonObj?.gpx?.wpt;
          const creator = jsonObj?.gpx?.creator;

          if (!parsedWaypointData || parsedWaypointData.length === 0) {
            dispatch(
              FlashMessageActions.FLASH_MESSAGE_SET({
                header: 'Invalid File',
                subheader: 'The file you uploaded does not contain any waypoints.',
              })
            );
            return;
          }

          if (parsedWaypointData) {
            transformData(parsedWaypointData, creator).then((data) => {
              const dataSortedByDate = _.orderBy(data, ['outing_date'], ['asc']);
              setWaypointData(dataSortedByDate);
            });
          }
        } catch (err) {
          console.error('Error parsing XML:', err);
        }
      };
      reader.readAsText(file);
    }
    event.target.value = ''; // Clear the input value so that the same file can be uploaded again
  };

  const importNotStarted = waypointData.length === 0 && !fileLoaded;

  const handleSubmitWaypoints = async (waypoints: WaypointLike[]) => {
    const waypointsWithWaterbodyRemoved = waypoints.map((waypoint: any) => {
      const { waterbody, ...rest } = waypoint;
      return rest as Omit<Waypoint, 'waterbody'>;
    });
    importWaypoints.mutate(waypointsWithWaterbodyRemoved, {
      onSuccess: () => {
        onShowImportScreenChange(false);
      },
      onSettled: () => {
        setWaypointData([]);
        setFileLoaded(false);
      },
    });
  };

  return (
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <span className={styles.heading}>
            Import Waypoints {waypointData.length > 0 && `(${waypointData.length})`}
          </span>
          <CloseOutlined
            onClick={() => {
              onClose();
              setWaypointData([]);
              setFileLoaded(false);
              onShowImportScreenChange(false);
            }}
          />
        </div>
        {waypointData.length > 0 ? (
          <ul className={styles.waypointList}>
            {waypointData.map((waypoint: any, i) => {
              return (
                <li className={styles.waypointItem} key={waypoint.lat + waypoint.lng + i}>
                  <span className={classNames(styles.waypointType, 'min-w-[46px]')}>
                    <WaypointMarker waypoint_type={'marker'} />
                  </span>
                  <div className={styles.waypointDetails}>
                    <strong>{waypoint?.name}</strong>

                    <span>{dayjs(waypoint.outing_date).format('MMM D, YYYY')}</span>
                    {waypoint.comments && <span>{waypoint.comments}</span>}
                    {waypoint.waterbody ? (
                      <span>{waypoint.waterbody.primary_name}</span>
                    ) : (
                      <span>
                        Lat: {waypoint.lat} Lng: {waypoint.lng}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <>
            {fileLoaded ? (
              <div style={{ padding: '40px 12px' }}>
                <Loading text={'Your file is being processed'} />
              </div>
            ) : (
              <div className={styles.noWaypoints}>
                <EnvironmentFilled />
                <small>Start by clicking the button below and selecting a .gpx file.</small>
              </div>
            )}
          </>
        )}
        <div className={styles.footerWrapper}>
          <div className={styles.footer}>
            {importNotStarted ? (
              <>
                <input
                  type="file"
                  id="fileInput"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  accept="text/gpx"
                  ref={fileInputRef}
                />
                <OmniaButton
                  onPress={() => fileInputRef.current.click()}
                  kind="secondary"
                  size="lg"
                  block
                >
                  Import File
                </OmniaButton>
              </>
            ) : (
              <OmniaButton
                onPress={() => {
                  handleSubmitWaypoints(waypointData);
                }}
                kind="primary"
                size="lg"
                block
                isDisabled={importWaypoints.isPending}
              >
                {importWaypoints.isPending ? (
                  <>
                    <LoadingOutlined style={{ fontSize: 24 }} spin /> Submitting now{' '}
                  </>
                ) : (
                  'Submit Waypoints'
                )}
              </OmniaButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
